import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchSheetRows } from "./utils/fetchSheetRows";
import { formatDate, formatDateWithTime, SHEET_URL } from "./constants";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { parseDMYDate } from "./utils/parseDMYDate";

const cardBaseStyle = {
  background: "#fff",
  borderRadius: "4px",
  boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
  padding: "2rem 2.5rem",
  minWidth: 200,
  textAlign: "center",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  margin: 0
};

function SystemsCardDetail({ user, selectedSchoolName, onLogout }) {
  const { type } = useParams();
  const [systems, setSystems] = useState([]);
  const [usageStats, setUsageStats] = useState({ total: 0, used: 0, percentage: 0 });
  const navigate = useNavigate();
  const selectedSchools = (Array.isArray(selectedSchoolName) ? selectedSchoolName : [selectedSchoolName || user.schoolName]).filter(Boolean);
  const schoolsForData = selectedSchools.length > 0 ? selectedSchools : [user.schoolName].filter(Boolean);

  // Function to get usage color based on percentage
  const getUsageColor = (percentage) => {
    if (percentage < 25) return "#F44336"; // Red
    if (percentage <= 40) return "#FFC107"; // Yellow
    return "#4CAF50"; // Green
  };

  // Function to calculate 15-day usage statistics for systems
  const calculateSystemUsageStats = (systemData) => {
    const now = new Date();
    const fifteenDaysAgo = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));

    // Only count active systems (exclude deactivated)
    const activeSystems = systemData.filter(system =>
      system.status && system.status.trim().toLowerCase() !== 'inactive'
    );

    let used = 0;
    const total = activeSystems.length;

    activeSystems.forEach(system => {
      // Check if used in last 15 days
      if (system.last_used_on) {
        const lastUsedDate = parseDMYDate(system.last_used_on);
        if (lastUsedDate && lastUsedDate >= fifteenDaysAgo) {
          used++;
        }
      }
    });

    const percentage = total > 0 ? Math.round((used / total) * 100) : 0;
    return { total, used, percentage };
  };

  useEffect(() => {
    async function fetchData() {
      const rows = await fetchSheetRows(SHEET_URL);
      const filtered = rows.filter(
        r =>
          schoolsForData.includes(r.schoolName) &&
          r.type?.toLowerCase() === "system" &&
          r.name && r.name.trim() !== ""
      );
      setSystems(filtered);

      // Calculate usage statistics
      const usage = calculateSystemUsageStats(filtered);
      setUsageStats(usage);
    }
    fetchData();
  }, [selectedSchools.join("|")]);

  // Filter systems based on card type
  let filteredSystems = systems;
  let cardLabel = "Total Systems";
  if (type === "active") {
    filteredSystems = systems.filter(t => t.status && t.status.trim().toLowerCase() === "active");
    cardLabel = "Total Active Systems";
  } else if (type === "not-activated") {
    filteredSystems = systems.filter(t => !t.status || t.status.trim() === "" || t.status.trim().toLowerCase() === "pending");
    cardLabel = "Total Not Activated";
  } else if (type === "deactivated") {
    filteredSystems = systems.filter(t => t.status && t.status.trim().toLowerCase() === "inactive");
    cardLabel = "Total Deactivated";
  } else if (type === "used-in-15-days") {
    const now = new Date();
    // Only show active systems who used in last 15 days
    filteredSystems = systems.filter(t => {
      // Only count active systems
      const isActive = t.status && t.status.trim().toLowerCase() !== 'inactive';
      if (!isActive) return false;

      if (!t.last_used_on) return false;
      const d = parseDMYDate(t.last_used_on);
      if (!d || isNaN(d.getTime())) return false;
      const diff = (now - d) / (1000 * 60 * 60 * 24);
      return diff <= 15;
    });
    cardLabel = "Used in Last 15 Days";
  }

  // PDF Export
  const exportAsPDF = () => {
    const doc = new jsPDF("p", "pt", "a4");
    let y = 30;

    doc.setFontSize(20);
    doc.setTextColor("#263D75");
    doc.text(cardLabel, 40, y);

    const tableColumn = [
      "Name", "Type", "Standard", "Volume", "License_key", "Password", "Status", "Last Used on", "DOB"
    ];
    const tableRows = filteredSystems.map(row => [
      row.name,
      row.type,
      row.standard,
      row.volume,
      row.license,
      row.password,
      row.status && row.status.trim() ? (row.status.trim().toLowerCase() === "inactive" ? "Deactivated" : row.status) : "Not Activated",
      row.last_used_on,
      row.dob
    ]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: y + 20,
      styles: { font: "poppins", fontSize: 9 }
    });

    doc.save(`${cardLabel.replace(/\s+/g, "_")}.pdf`);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f6fbff",
      fontFamily: "'Poppins', 'Roboto', sans-serif",
      overflowY: "auto"
    }}>
      <header
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #263D75 60%, #4F6CB6 100%)",
          color: "#fff",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
          boxShadow: "0 4px 16px 0 rgba(38,61,117,0.18), 0 1.5px 4px 0 rgba(38,61,117,0.10)",
          borderBottomLeftRadius: "18px",
          borderBottomRightRadius: "18px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#fff",
            color: "#263D75",
            border: "none",
            borderRadius: "8px",
            padding: "0.4rem 1.1rem",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: "1rem",
            boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
            marginRight: 18,
            transition: "background 0.2s, box-shadow 0.2s",
            outline: "none",
            display: "flex",
            alignItems: "center"
          }}
        >
          &#8592; Back
        </button>
        <div style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          fontWeight: "bold",
          fontSize: "1.3rem",
          letterSpacing: "1px",
          color: "#fff",
          textAlign: "center",
          pointerEvents: "none",
          minWidth: 200,
          maxWidth: "40vw",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          {cardLabel}
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              background: "linear-gradient(90deg, #fff 60%, #eaf4ff 100%)",
              color: "#263D75",
              border: "none",
              borderRadius: "8px",
              padding: "0.55rem 1.4rem",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "1rem",
              boxShadow: "0 2px 8px rgba(38,61,117,0.10)",
              transition: "background 0.2s, box-shadow 0.2s",
              outline: "none",
            }}
          >
            Logout
          </button>
        )}
      </header>
      <div style={{ padding: "2rem" }}>
        {/* Used in Last 15 Days Card - Only show when type is used-in-15-days */}
        {type === "used-in-15-days" && (
          <div style={{
            marginBottom: "1.5rem",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
            padding: "1.5rem"
          }}>
            <h3 style={{
              color: "#263D75",
              marginBottom: "1rem",
              fontSize: "1.2rem",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}>
              <span style={{ fontSize: "1.1rem" }}>💻</span>
              Systems - Used in Last 15 Days
            </h3>

            <div style={{
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(38,61,117,0.12)",
              padding: "1.5rem",
              maxWidth: "280px",
              textAlign: "center",
              borderLeft: `4px solid ${getUsageColor(usageStats.percentage)}`,
              transition: "transform 0.2s ease",
            }}>
              <div style={{
                fontSize: "2.8rem",
                color: getUsageColor(usageStats.percentage),
                fontWeight: "700",
                marginBottom: "0.5rem"
              }}>
                {usageStats.percentage}%
              </div>
              <div style={{
                color: "#263D75",
                fontWeight: "600",
                fontSize: "1rem",
                marginBottom: "0.3rem"
              }}>
                Systems Used
              </div>
              <div style={{
                color: "#666",
                fontSize: "0.85rem",
                fontWeight: "500"
              }}>
                {usageStats.used} of {usageStats.total} active
              </div>
            </div>
          </div>
        )}

        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.4rem"
        }}>
          <h1 style={{ color: "#263D75", margin: 0 }}>
            {cardLabel}
          </h1>
          <button
            onClick={exportAsPDF}
            style={{
              background: "#263D75",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1.2rem",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "1rem",
              marginLeft: "1.2rem"
            }}
          >
            Export as PDF
          </button>
        </div>
        <div style={{
          marginTop: "0.5rem",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(38,61,117,0.08)",
          padding: "1.5rem",
          overflowX: "auto"
        }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "'Poppins', 'Roboto', sans-serif",
            fontSize: "1rem"
          }}>
            <thead>
              <tr style={{ background: "#eaf4ff" }}>
                <th style={{ padding: "0.7rem", textAlign: "left", borderRadius: "6px 0 0 6px" }}>Name</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Type</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Standard</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Volume</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>License_key</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Password</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Status</th>
                <th style={{ padding: "0.7rem", textAlign: "left" }}>Last Used on</th>
                <th style={{ padding: "0.7rem", textAlign: "left", borderRadius: "0 6px 6px 0" }}>DOB</th>
              </tr>
            </thead>
            <tbody>
              {filteredSystems.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", color: "#aaa", padding: "1.5rem" }}>
                    No systems found for this category.
                  </td>
                </tr>
              )}
              {filteredSystems.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "0.6rem" }}>{row.name}</td>
                  <td style={{ padding: "0.6rem", textTransform: "capitalize" }}>{row.type}</td>
                  <td style={{ padding: "0.6rem" }}>{row.standard}</td>
                  <td style={{ padding: "0.6rem" }}>{row.volume}</td>
                  <td style={{ padding: "0.6rem" }}>{row.license}</td>
                  <td style={{ padding: "0.6rem" }}>{row.password}</td>
                  <td style={{ padding: "0.6rem" }}>
                    {
                      row.status && row.status.trim()
                        ? (row.status.trim().toLowerCase() === "inactive" ? "Deactivated" : row.status)
                        : "Not Activated"
                    }
                  </td>
                  <td style={{ padding: "0.6rem" }}>{row.last_used_on}</td>
                  <td style={{ padding: "0.6rem" }}>{row.dob}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SystemsCardDetail;